import http from '../../../http-common.js';

// Monotonic id for getPayInList requests. Overlapping calls (typing while
// the initial load is in flight, rapid refresh clicks) resolve in ARBITRARY
// order — without this, whichever response lands last overwrites the list,
// even when it belongs to an older request (stale search results, status
// flip-flops between refreshes). Only the latest request may commit.
let payInListRequestSeq = 0;
// AbortController of the in-flight request (24082026): superseding must
// also CANCEL the old request, not just ignore its response — the seq
// guard alone left the stale request occupying one of the browser's 6
// per-host connection slots until the server eventually answered.
// Stacked slow list calls + socket long-polling wedged the entire SPA
// (reproduced with scripts/hostile-network-proxy.js). Aborting frees the
// slot immediately.
let payInListInflight = null;

export default {
  async getPayInList(context, params) {
    const requestSeq = ++payInListRequestSeq;
    if (payInListInflight) payInListInflight.abort();
    const controller = new AbortController();
    payInListInflight = controller;
    const username = context.rootGetters.username;
    const role = context.rootGetters.role;

    let url = `/orders`;

    let questionMark = '?';

    if (role == 'user') {
      url += `${questionMark}validatorUsername=${username}`;
      questionMark = '&';
    }

    if (params.type) {
      url += `${questionMark}type=${params.type}`;
      questionMark = '&';
    }

    if (params.payoutType) {
      url += `${questionMark}payout_type=${params.payoutType}`;
    }

    if (params.transactionType) {
      url += `${questionMark}transactionType=${params.transactionType}`;
      questionMark = '&';
    }

    if (params.created_by) {
      url += `${questionMark}created_by=${params.created_by}`;
      questionMark = '&';
    }

    if (params.amount) {
      url += `${questionMark}amount=${params.amount}`;
      questionMark = '&';
    }

    // Range bounds — used by the payout list. The backend extracts these
    // ahead of the equality loop and applies `amount >= ?` / `amount <= ?`.
    if (params.amountMin !== undefined && params.amountMin !== null && params.amountMin !== '') {
      url += `${questionMark}amountMin=${params.amountMin}`;
      questionMark = '&';
    }
    if (params.amountMax !== undefined && params.amountMax !== null && params.amountMax !== '') {
      url += `${questionMark}amountMax=${params.amountMax}`;
      questionMark = '&';
    }

    if (params.paymentStatus) {
      url += `${questionMark}paymentStatus=${params.paymentStatus}`;
      questionMark = '&';
    }

    if (params.validatorUsername) {
      url += `${questionMark}validatorUsername=${params.validatorUsername}`;
      questionMark = '&';
    }

    if (params.searchField && params.searchField !== 'all') {
      url += `${questionMark}searchField=${encodeURIComponent(params.searchField)}`;
      questionMark = '&';
    }
    if (params.searchKeyWord) {
      url += `${questionMark}searchKeyWord=${params.searchKeyWord}`;
      questionMark = '&';
    }

    if (params.startDate && params.endDate) {
      url += `${questionMark}startDate=${params.startDate}&endDate=${params.endDate}`;
      questionMark = '&';
    }

    if (params.paymentMethod) {
      url += `${questionMark}paymentMethod=${params.paymentMethod}`;
      questionMark = '&';
    }

    if (params.approval_method) {
      url += `${questionMark}approval_method=${params.approval_method}`;
      questionMark = '&';
    }

    // Vendor-level filters (admin / superadmin). Both empty strings are
    // skipped by the truthy check so non-vendor callers passing the keys
    // through don't poison the URL with empty params.
    if (params.branchId) {
      url += `${questionMark}branchId=${params.branchId}`;
      questionMark = '&';
    }
    if (params.clientName) {
      url += `${questionMark}clientName=${encodeURIComponent(params.clientName)}`;
      questionMark = '&';
    }

    url += `${questionMark}first=${params.first}&page=${params.page}`;

    let response;
    try {
      response = await http.get(url, { signal: controller.signal });
    } catch (err) {
      // Aborted = superseded by a newer request; the newer one owns the
      // UI state — stay silent (the request-side mirror of the seq-guard
      // response drop below).
      if (err && (err.code === 'ERR_CANCELED' || err.name === 'CanceledError')) return;
      throw err;
    } finally {
      if (payInListInflight === controller) payInListInflight = null;
    }

    // A newer request started while this one was in flight — its response
    // is stale; drop it instead of overwriting fresher data.
    if (requestSeq !== payInListRequestSeq) return;

    if (response.status == 200) {
      const responseData = response.data.data;
      const pagination = response.data.pagination;
      context.commit('setPayInList', responseData);
      context.commit('setPagination', pagination);
    } else {
      const error = new Error('Unauthorized: You are not an admin.');
      throw error;
    }
  },
  async approveOrder(context, payload) {
    const refID = payload.refID;
    const data = JSON.stringify({
      transactionID: payload.transactionID
    });

    try {
      const response = await http.post(`/orders/${refID}`, data);

      if (response.status == 200) {
        context.commit('setPaymentStatusAndUtr', payload);
      } else {
        const error = new Error('Something went wrong!');
        throw error;
      }
    } catch (error) {
      const errorMessage = new Error(
        error.response.data.message || 'Something failed!'
      );
      throw errorMessage;
    }
  },
  async approvePayOutOrder(context, payload) {
    const refID = payload.refID;
    const data = payload.formData || JSON.stringify({
      paymentStatus: payload.paymentStatus,
      transactionID: payload.transactionID,
      payout_type: payload.payout_type,
      instant_balance: payload.instant_balance
    });

    const config = payload.formData ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : {};

    try {
      const response = await http.post(
        `/orders/${refID}/approvePayOutOrder`,
        data,
        config
      );
      if (response.status == 200) {
        // Server tells us whether this settlement closed the order
        // (isClosing=true → paymentStatus='approved' terminal) or whether
        // the order returned to the available pool (isClosing=false →
        // paymentStatus='unassigned'). Without using the server-reported
        // flag, the local store would briefly show partials as 'approved'
        // before the parent list refresh corrects it.
        const serverIsClosing = response.data && response.data.isClosing === true;
        const postPaymentStatus = serverIsClosing ? 'approved' : 'unassigned';
        context.commit('setPayOutOrderPaymentStatus', {
          refID: refID,
          paymentStatus: postPaymentStatus,
          transactionID: payload.formData ? payload.formData.get('transactionID') : payload.transactionID
        });
      } else {
        const error = new Error('Something went wrong!');
        throw error;
      }
    } catch (error) {
      const errorMessage = new Error(
        error.response.data.message || 'Something failed!'
      );
      throw errorMessage;
    }
  },
  async rejectPayoutOrder(context, payload) {
    const refID = payload.refID;
    try {
      const response = await http.post(
        `/orders/${refID}/reject-payout`,
        { rejection_reason: payload.rejection_reason }
      );
      if (response.status == 200 && response.data.success) {
        // Order goes back to 'unassigned' so other branches can pick it up.
        context.commit('setPayOutOrderPaymentStatus', {
          refID: refID,
          paymentStatus: 'unassigned',
          transactionID: ''
        });
        return response.data;
      } else {
        throw new Error(response.data.message || 'Something went wrong!');
      }
    } catch (error) {
      const errorMessage = new Error(
        (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Something failed!'
      );
      throw errorMessage;
    }
  },
  async vendorRejectPayoutOrder(context, payload) {
    // Vendor-level rejection: terminates the payout (failed) and fires the
    // merchant failed callback. Use rejectPayoutOrder above for the branch
    // flow (back to unassigned).
    const refID = payload.refID;
    try {
      const response = await http.post(
        `/orders/${refID}/vendor-reject-payout`,
        { rejection_reason: payload.rejection_reason }
      );
      if (response.status == 200 && response.data.success !== false) {
        context.commit('setPayOutOrderPaymentStatus', {
          refID: refID,
          paymentStatus: 'failed',
          transactionID: ''
        });
        return response.data;
      } else {
        throw new Error(response.data.message || 'Something went wrong!');
      }
    } catch (error) {
      const errorMessage = new Error(
        (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Something failed!'
      );
      throw errorMessage;
    }
  },
  async forceCompletePayoutOrder(context, payload) {
    // Force-complete: terminate a partial-settled payout. The merchant gets
    // a single terminal callback (paymentStatus='approved' with
    // remaining_amount > 0). Distinct from vendorRejectPayoutOrder above
    // which marks 'failed'; force-complete acknowledges that money DID move
    // and writes off the remainder.
    const refID = payload.refID;
    try {
      const response = await http.post(
        `/orders/${refID}/force-complete-payout`,
        { reason: payload.reason }
      );
      if (response.status == 200 && response.data.success !== false) {
        context.commit('setPayOutOrderPaymentStatus', {
          refID: refID,
          paymentStatus: 'approved',
          transactionID: ''
        });
        return response.data;
      } else {
        throw new Error(response.data.message || 'Something went wrong!');
      }
    } catch (error) {
      const errorMessage = new Error(
        (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Something failed!'
      );
      throw errorMessage;
    }
  },
  async updatePayInUtr(_context, payload) {
    // Vendor correction of UTR on an approved pay-in order. Backend
    // chains to callbackHook so the merchant gets a corrected callback.
    const refID = payload.refID;
    try {
      const response = await http.post(
        `/orders/${refID}/update-payin-utr`,
        { transactionID: payload.transactionID, reason: payload.reason }
      );
      if (response.status === 200) return response.data;
      throw new Error(response.data.message || 'Something went wrong.');
    } catch (error) {
      const errorMessage = new Error(
        (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Something failed!'
      );
      throw errorMessage;
    }
  },
  async markPayInFailed(_context, payload) {
    // Vendor flips a pay-in order to 'failed' from pending / expired /
    // approved. Backend reverses bank credit on approved, then fires the
    // merchant failed callback.
    const refID = payload.refID;
    try {
      const response = await http.post(
        `/orders/${refID}/mark-payin-failed`,
        { reason: payload.reason }
      );
      if (response.status === 200 && response.data.success !== false) {
        return response.data;
      }
      throw new Error(response.data.message || 'Something went wrong.');
    } catch (error) {
      const errorMessage = new Error(
        (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Something failed!'
      );
      throw errorMessage;
    }
  },
  async changePayInOrderBank(_context, payload) {
    // Vendor-initiated manual bank swap on a pay-in order. For approved
    // orders the backend reverses the old bank's credit and applies the
    // new bank's atomically — UI just shows success + the parent list
    // refresh picks up the new validator + amount columns.
    const refID = payload.refID;
    try {
      const response = await http.post(
        `/orders/${refID}/change-payin-bank`,
        {
          newValidatorUsername: payload.newValidatorUsername,
          reason: payload.reason,
        }
      );
      if (response.status === 200 && response.data.success !== false) {
        return response.data;
      }
      throw new Error(response.data.message || 'Something went wrong.');
    } catch (error) {
      const errorMessage = new Error(
        (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Something failed!'
      );
      throw errorMessage;
    }
  },
  async getPayoutSplits(_context, payload) {
    // Fetch the per-settlement timeline for a payout order. Backend
    // role-scopes the rows + fields (vendor/superadmin see full list with
    // commission; client sees rows without commission; subadmin sees only
    // their own settlements + force_complete rows).
    const refID = payload.refID;
    try {
      const response = await http.get(`/orders/${refID}/payout-splits`);
      if (response.status == 200 && response.data.success !== false) {
        return response.data.data || [];
      }
      throw new Error(response.data.message || 'Failed to load splits.');
    } catch (error) {
      const errorMessage = new Error(
        (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Something failed!'
      );
      throw errorMessage;
    }
  },
  async getOrderStatics(context, params) {
    try {
      const response = await http.get(
        `/orders/getOrderStatics/${params.type}?selectedDay=${params.selectedDay}&website=${params.website}&startDate=${params.startDate}&endDate=${params.endDate}&vendor=${params.vendor}&orderCreator=${params.orderCreator}`
      );
      if (response.status == 200) {
        let payinArr = [];
        let payoutArr = [];

        const responseData = response.data.data;

        responseData.forEach(obj => {
          if (obj.type === 'payin') {
            const arrObj = {
              paymentStatus: obj.paymentStatus,
              count: obj.count,
              totalAmount: obj.total_amount,
              totalDiffAmount: obj.total_diff_amount
            };
            payinArr.push(arrObj);
          } else if (obj.type === 'payout') {
            const arrObj = {
              paymentStatus: obj.paymentStatus,
              count: obj.count,
              totalAmount: obj.total_amount
            };
            payoutArr.push(arrObj);
          }
        });

        context.commit('setPayInOrderStatics', payinArr);
        context.commit('setPayOutOrderStatics', payoutArr);
      } else {
        const error = new Error('Something went wrong!');
        throw error;
      }
    } catch (error) {
      const errorMessage = new Error(
        error.response.data.message || 'Something failed!'
      );
      throw errorMessage;
    }
  },
  async getBarChartData(context, params) {
    try {
      const response = await http.get(
        `/orders/getBarChartData/${params.type}?selectedDay=${params.selectedDay}&website=${params.website}&startDate=${params.startDate}&endDate=${params.endDate}&paymentType=${params.paymentType}&vendor=${params.vendor}&orderCreator=${params.orderCreator}`
      );
      if (response.status == 200) {
        const data = response.data.data;

        let series = [];
        let paymentStatusData = {};

        // Loop through data and create an object to hold the data for each payment status
        for (let i = 0; i < data.length; i++) {
          let paymentStatus = data[i].paymentStatus;
          if (
            !Object.prototype.hasOwnProperty.call(
              paymentStatusData,
              paymentStatus
            )
          ) {
            paymentStatusData[paymentStatus] = {
              name:
                paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1),
              data: Array.from({ length: 24 }).fill(0)
            };
          }
        }

        // Loop through hours and add the total amount for each payment status to the corresponding hour in the data array
        for (let i = 0; i < 24; i++) {
          for (let j = 0; j < data.length; j++) {
            let paymentStatus = data[j].paymentStatus;
            let hour = data[j].hour;
            let totalAmount = parseFloat(data[j].totalAmount);
            if (i === hour) {
              paymentStatusData[paymentStatus].data[i] += totalAmount;
            }
          }
        }

        // Push the data for each payment status into the series array
        for (let paymentStatus in paymentStatusData) {
          if (paymentStatus == 'approved') {
            series.push(paymentStatusData[paymentStatus]);
          }
        }

        context.commit('setBarChartDataSeries', series);
      } else {
        const error = new Error('Something went wrong!');
        throw error;
      }
    } catch (error) {
      const errorMessage = new Error(
        error.response.data.message || 'Something failed!'
      );
      throw errorMessage;
    }
  }
};
 